/**
 * The frame every authenticated screen sits inside.
 *
 * It renders only when there is a session. The alternative — rendering the
 * shell and letting each screen discover it is not allowed — briefly shows the
 * structure of an account to somebody who is not signed in, and briefly is
 * long enough to read.
 */
import { Outlet, useLocation } from "react-router";

import { ErrorBoundary } from "@/design-system/ErrorBoundary";
import { ScopeSwitcher } from "@/features/auth/ScopeSwitcher";
import { useSignOut } from "@/features/auth/useSession";

import { MobileNav } from "./MobileNav";
import { StatusBar } from "./StatusBar";
import {
  TopBar,
  type Connection,
  type Environment,
  type Source,
} from "./TopBar";
import { useTheme } from "./useTheme";

type Props = {
  readonly platform?: boolean;
  /** The client's cabinet: its own four sections. */
  readonly client?: boolean;
  readonly login: string;
  readonly accountId: string;
  readonly roles: readonly string[];
  readonly connection: Connection;
  readonly environment: Environment;
  readonly equity?: string | undefined;
  readonly risk?: string | undefined;
  readonly sources?: readonly Source[] | undefined;
  /** Above every page of this shell, e.g. the read-only client view. */
  readonly notice?: React.ReactNode;
};

export function AppShell({
  platform = false,
  client = false,
  login,
  accountId,
  roles,
  connection,
  environment,
  equity,
  risk,
  sources,
  notice,
}: Props): React.JSX.Element {
  const { theme, toggle } = useTheme();
  const signOut = useSignOut();

  return (
    <div className="flex min-h-dvh flex-col bg-background-base">
      <TopBar
        platform={platform}
        client={client}
        login={login}
        accountId={accountId}
        roles={roles}
        connection={connection}
        environment={environment}
        equity={equity}
        risk={risk}
        sources={sources}
        theme={theme}
        onToggleTheme={toggle}
        onSignOut={signOut.submit}
        signingOut={signOut.pending}
        scope={<ScopeSwitcher />}
      />

      {/* Padded at the bottom for whichever bar is present, so neither ever
          covers the last row of a table: the mobile navigation below the shell
          breakpoint, the status bar above it. Tying the padding to a different
          width than the bars would leave a range where the last row sits under
          one of them.

          The working area stops at 1600px and centres. Past that width a
          twelve-column row stretches until a symbol and the figures belonging
          to it are a hand's width apart, which is the defect §4 records for a
          single column, arriving through the page instead. One gutter at every
          width — 16px — because the density of §4 is a ratio between three
          numbers and changing one of them by breakpoint is what made a screen
          read as cramped at one size and loose at the next. */}
      <main className="mx-auto w-full min-w-0 max-w-[1600px] flex-1 p-4 pb-20 shell:pb-[38px]">
        {notice}
        <PageBoundary />
      </main>

      <MobileNav platform={platform} client={client} />
      <StatusBar client={client} />
    </div>
  );
}


/** A page that fails to render shows why; the menu and the bars stay. */
function PageBoundary(): React.JSX.Element {
  const { pathname } = useLocation();
  return <ErrorBoundary resetKey={pathname}><Outlet /></ErrorBoundary>;
}
